import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'

import ModalPinCode from '../SettingPageComponents/ModalPinCode'

import EventForm from './EventForm'
import DateBlock from './DateBlock'
import {
  insertTimeDuration,
  decodeScheduleUI,
} from './EventTools'

import styles from '../../../../css/scheduler.module.css'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import AppStore from '../../../stores/AppStore'

export default function Scheduler (props) {
  const { deviceID } = useParams()
  const { level } = AppStore.getUser()?.permissions || { level: 0 }

  const {
    scheduleNumber,
    schedules = [],
    pinCodeCancelled,
    pinCodeStatus,
    pinCodeRequired = false,
    limit,
  } = props

  let schedule = { key: scheduleNumber, events: [] }
  const foundSchedule = schedules.findIndex(e => e.key === scheduleNumber)
  if (foundSchedule > -1) {
    schedule = schedules[foundSchedule]
  }

  const [state, setState] = useState({
    showEventForm: false,
    showTooManyEvents: false,
    events: schedule.events,
    updateIndex: -1,
    pinCodeNow: false,
    eventFormDay: (new Date()).getDay(),
  })

  const { events, showEventForm, showTooManyEvents, updateIndex, pinCodeNow, eventFormDay } = state
  const shouldSaveRef = useRef(false)

  const getNewData = useCallback(() => {
    AppDispatcher.dispatch({
      action: 'get-device',
      deviceID,
    })
  }, [deviceID])

  useEffect(() => {
    if (
      pinCodeCancelled === true &&
      pinCodeStatus !== 'authenticated'
    ) {
      setState(prev => ({
        ...prev,
        showEventForm: false,
        updateIndex: -1,
        pinCodeNow: false,
      }))
    }
  }, [pinCodeCancelled, pinCodeStatus])

  const dates = (() => {
    const sortedEvents = [...events].sort((a, b) => a - b)
    const schedule = decodeScheduleUI(sortedEvents, (new Date()).getTimezoneOffset())
    const days = [
      { day: 0, label: 'Sun', data: [], crossInto: false, crossOutOf: false, today: false, },
      { day: 1, label: 'Mon', data: [], crossInto: false, crossOutOf: false, today: false, },
      { day: 2, label: 'Tue', data: [], crossInto: false, crossOutOf: false, today: false, },
      { day: 3, label: 'Wed', data: [], crossInto: false, crossOutOf: false, today: false, },
      { day: 4, label: 'Thu', data: [], crossInto: false, crossOutOf: false, today: false, },
      { day: 5, label: 'Fri', data: [], crossInto: false, crossOutOf: false, today: false, },
      { day: 6, label: 'Sat', data: [], crossInto: false, crossOutOf: false, today: false, },
    ]
    days[(new Date()).getDay()].today = true
    if (!schedule) return days
    schedule.forEach((s) => {
      if (s.duration.crossWeek && s.duration.cross) {
        days[s.start.day].data.unshift(s)
      } else {
        days[s.start.day].data.push(s)
      }
      if (s.duration.cross) days[s.stop.day].crossInto = true
      if (s.duration.cross) days[s.start.day].crossOutOf = true
    })
    for (let i = 0; i < days.length; i++) {
      days[i].data.sort((a, b) => {
        const timeA = a.start.hr * 60 + a.start.min
        const timeB = b.start.hr * 60 + b.start.min
        return timeA - timeB
      })
    }
    return days
  })()

  const saveToServer = useCallback(() => {
    const { address, scheduleNumber: schedNum = 1 } = props
    const schedulesNew = [...schedules]
    const schedule = { key: schedNum, events }
    const foundSchedule = schedulesNew.findIndex(e => e.key === schedNum)
    if (foundSchedule > -1) {
      schedulesNew[foundSchedule] = schedule
    } else {
      schedulesNew.push(schedule)
    }

    if (false) { // check for bad settings here
      // do nothing
    } else {
      AppDispatcher.dispatch({
        action: 'set-settings',
        settings: [{
          address,
          status: 'new',
          settings: {
            schedules: schedulesNew,
          },
        }],
      })
    }
  }, [events, schedules, props])

  useEffect(() => {
    if (shouldSaveRef.current) {
      shouldSaveRef.current = false
      saveToServer()
    }
  }, [events, saveToServer])

  const addEvent = useCallback((event) => {
    const { day, hr, min, duration } = event
    const eventsNew = insertTimeDuration(
      [...events],
      day, hr, min,
      duration,
      (new Date()).getTimezoneOffset()
    )
    shouldSaveRef.current = true
    setState(prev => ({
      ...prev,
      events: eventsNew,
      showEventForm: false,
      updateIndex: -1,
    }))
  }, [events])

  const updateEvent = useCallback((key, newEvent) => {
    const { day, hr, min, duration } = newEvent
    let eventsNew = JSON.parse(JSON.stringify(events))
    eventsNew.splice(key, 1)

    eventsNew = insertTimeDuration(
      [...eventsNew],
      day, hr, min,
      duration,
      (new Date()).getTimezoneOffset()
    )

    shouldSaveRef.current = true
    setState(prev => ({
      ...prev,
      events: eventsNew,
      showEventForm: false,
      updateIndex: -1,
    }))
  }, [events])

  const removeEvent = useCallback((key) => {
    const eventsNew = JSON.parse(JSON.stringify(events))
    eventsNew.splice(key, 1)

    shouldSaveRef.current = true
    setState(prev => ({
      ...prev,
      events: eventsNew,
      showEventForm: false,
      updateIndex: -1,
    }))
  }, [events])

  const handleUpdateEvent = useCallback((i) => {
    setState(prev => ({
      ...prev,
      showEventForm: true,
      updateIndex: i,
      pinCodeNow: true,
    }))
  }, [])

  const eventFormOn = useCallback((day) => {
    if (events && events.length >= limit) {
      tooManyEventsFormOn()
    } else if (level >= 10) {
      setState(prev => ({
        ...prev,
        showEventForm: true,
        updateIndex: -1,
        eventFormDay: day >= 0 && day <= 6 ? day : (new Date()).getDay(),
        pinCodeNow: true,
      }))
    }
  }, [events, limit, level])

  const eventFormOff = useCallback(() => {
    setState(prev => ({
      ...prev,
      showEventForm: false,
      updateIndex: -1,
      pinCodeNow: false,
      eventFormDay: (new Date()).getDay(),
    }))
  }, [])

  const tooManyEventsFormOn = useCallback(() => {
    setState(prev => ({
      ...prev,
      showTooManyEvents: true,
    }))
  }, [])

  const tooManyEventsFormOff = useCallback(() => {
    setState(prev => ({
      ...prev,
      showTooManyEvents: false,
    }))
  }, [])

  const authenticated = pinCodeRequired ? pinCodeStatus === 'authenticated' : true

  return (
    <div>
      <ModalPinCode
        required={pinCodeRequired && pinCodeNow}
        status={pinCodeStatus}
        pageChange
      />
      <div className={styles.scheduler}>
        {(!authenticated || !showEventForm) && (
          <div>
            <div className={styles.scheduler_nav}>
              <span className={styles.scheduler_nav_space} />
              <span>
                Schedule&nbsp;
                {scheduleNumber}
              </span>
              <button
                className={styles.scheduler_nav_plus}
                onClick={eventFormOn}
                type='button'
              >
                {level >= 10 ? '+' : ''}
              </button>
            </div>
            <div className={styles.scheduler_dates}>
              {dates.map(d => (
                <DateBlock
                  key={d.label}
                  day={d.day}
                  label={d.label}
                  data={d.data}
                  crossInto={d.crossInto}
                  crossOutOf={d.crossOutOf}
                  today={d.today}
                  handleUpdateEvent={handleUpdateEvent}
                  newEvent={eventFormOn}
                />
              ))}
            </div>
          </div>
        )}
        {authenticated && showEventForm && (
          <div className={styles.scheduler_form_background}>
            <div className={styles.scheduler_form}>
              <div className={styles.scheduler_form_nav}>
                <button
                  className={styles.scheduler_form_nav_chevron}
                  onClick={eventFormOff}
                  type='button'
                >
                  <i className='fa fa-chevron-left fa-lg' aria-hidden='true' />
                </button>
                <span>Edit Time Block</span>
                <span className={styles.scheduler_form_nav_space} />
              </div>
              <EventForm
                events={events}
                addEvent={addEvent}
                updateEvent={updateEvent}
                removeEvent={removeEvent}
                updateIndex={updateIndex}
                eventFormDay={eventFormDay}
              />
            </div>
          </div>
        )}
        {showTooManyEvents && (
          <div className={styles.scheduler_form_background}>
            <div className={styles.scheduler_form}>
              <div className={styles.scheduler_form_nav}>
                <button
                  className={styles.scheduler_form_nav_chevron}
                  onClick={tooManyEventsFormOff}
                  type='button'
                >
                  <i className='fa fa-chevron-left fa-lg' aria-hidden='true' />
                </button>
                <span>Max Timeblocks</span>
                <span className={styles.scheduler_form_nav_space} />
              </div>
              <h4
                style={{
                  padding: '30px',
                  lineHeight: '30px',
                  maxWidth: '460px',
                  margin: 'auto',
                }}
              >
                This device allows for up to
                &nbsp;
                {limit}
                &nbsp;
                timeblocks per schedule. You have used all available timeblocks. Please delete unused timeblocks or contact&nbsp;
                <a href='mailto:support@altrac.io'>support@altrac.io</a>
                &nbsp;
                for more information.
              </h4>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

Scheduler.propTypes = {
  scheduleNumber: PropTypes.number.isRequired,
  address: PropTypes.string.isRequired,
  pinCodeCancelled: PropTypes.bool.isRequired,
  pinCodeStatus: PropTypes.string.isRequired,
  pinCodeRequired: PropTypes.bool,
  limit: PropTypes.number,
}
